/**
 * reportDrafts.js — СОХРАНЁННЫЕ ЗАКЛЮЧЕНИЯ. [11.1.31]
 *
 * Просьба владельца: «сохранять заключение для повторной загрузки и
 * редактирования в динамике». Смысл именно динамический: через полгода
 * пациент приходит на контроль, врач поднимает прошлое заключение, правит
 * размеры и пишет «ранее 17×16 мм» — не набирая всё заново.
 *
 * Хранится ПОЛЕ В ПОЛЕ, а не готовый документ: файл .docx уже нельзя
 * отредактировать в программе, а набор полей — можно. Ключ записи — сам
 * пациент и дата исследования, поэтому повторные сохранения одного и того
 * же исследования не плодят близнецов.
 *
 * БЕЗ ИМПОРТОВ ЯДРА: чистые функции + localStorage, проверяются verify на
 * подставном хранилище.
 */

const КЛЮЧ = 'mikhmel_report_drafts';
const ПРЕДЕЛ = 200;   // записей; поля заключения — это килобайты, не мегабайты

const _хранилище = () => {
  try { return typeof localStorage !== 'undefined' ? localStorage : null; }
  catch (e) { return null; }
};

/** @returns {{ключ:string, когда:number, подпись:string, данные:object}[]} */
export function прочитатьЗаключения() {
  const х = _хранилище();
  if (!х) return [];
  try {
    const с = JSON.parse(х.getItem(КЛЮЧ) || '[]');
    return Array.isArray(с) ? с.filter((з) => з && з.ключ && з.данные) : [];
  } catch (e) { return []; }
}

function _записать(список) {
  const х = _хранилище();
  if (!х) return false;
  try { х.setItem(КЛЮЧ, JSON.stringify(список.slice(0, ПРЕДЕЛ))); return true; }
  catch (e) { return false; }   // приватный режим или переполнение
}

/** Ключ записи: пациент + дата исследования + № истории. */
export function ключЗаключения(д = {}) {
  const ч = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
  return [ч(д.patientName), ч(д.studyDate), ч(д.номерИстории)].filter(Boolean).join('|') || 'без-имени';
}

/** Человеческая подпись строки списка. */
export function подписьЗаключения(д = {}) {
  const части = [д.patientName || 'Без имени'];
  if (д.studyDate) части.push(д.studyDate);
  if (д.modality) части.push(д.modality);
  if (д.номерИстории) части.push('№ ' + д.номерИстории);
  return части.join(' · ');
}

/**
 * Сохранить (или обновить одноимённое) заключение.
 * @returns {{ок:boolean, всего?:number, обновлено?:boolean, причина?:string}}
 */
export function сохранитьЗаключение(данные) {
  if (!данные || typeof данные !== 'object') return { ок: false, причина: 'Нечего сохранять' };
  const ключ = ключЗаключения(данные);
  const было = прочитатьЗаключения();
  const обновлено = было.some((з) => з.ключ === ключ);
  const список = [
    { ключ, когда: Date.now(), подпись: подписьЗаключения(данные), данные },
    ...было.filter((з) => з.ключ !== ключ),
  ];
  if (!_записать(список)) return { ок: false, причина: 'Хранилище недоступно (приватный режим?)' };
  return { ок: true, всего: список.length, обновлено };
}

export function загрузитьЗаключение(ключ) {
  return прочитатьЗаключения().find((з) => з.ключ === ключ) || null;
}

export function удалитьЗаключение(ключ) {
  return _записать(прочитатьЗаключения().filter((з) => з.ключ !== ключ));
}

/** Файл обмена — чтобы унести заключение на другой компьютер или в архив. */
export function собратьФайлЗаключения(запись) {
  return JSON.stringify({ формат: 'mikhmel-report', версия: 1, запись }, null, 2);
}

/**
 * Разбор файла. Принимает и одну запись, и пачку, и голые данные полей —
 * врач не обязан помнить, что именно выгружал (правило файла профилей).
 * @returns {{ок:boolean, записи?:object[], причина?:string}}
 */
export function разобратьФайлЗаключения(текст) {
  let д;
  try { д = JSON.parse(String(текст || '')); }
  catch (e) { return { ок: false, причина: 'Это не файл заключения: JSON не читается' }; }
  const норм = (з) => {
    const данные = з?.данные || з?.data || з;
    if (!данные || typeof данные !== 'object') return null;
    return { ключ: з?.ключ || ключЗаключения(данные), когда: з?.когда || Date.now(), подпись: з?.подпись || подписьЗаключения(данные), данные };
  };
  const собрать = (м) => м.map(норм).filter(Boolean);
  if (Array.isArray(д)) { const з = собрать(д); return з.length ? { ок: true, записи: з } : { ок: false, причина: 'В файле нет заключений' }; }
  if (д?.запись) { const з = собрать([д.запись]); return з.length ? { ок: true, записи: з } : { ок: false, причина: 'Запись пуста' }; }
  if (Array.isArray(д?.записи)) { const з = собрать(д.записи); return з.length ? { ок: true, записи: з } : { ок: false, причина: 'В файле нет заключений' }; }
  const одно = норм(д);
  return одно ? { ок: true, записи: [одно] } : { ок: false, причина: 'В файле нет ни заключения, ни его полей' };
}

/** Слить импортированное со своим: чужой файл не стирает свои записи. */
export function слитьЗаключения(новые) {
  const было = прочитатьЗаключения();
  const слито = [...новые, ...было.filter((с) => !новые.some((н) => н.ключ === с.ключ))]
    .sort((a, b) => (b.когда || 0) - (a.когда || 0));
  return _записать(слито) ? { ок: true, всего: слито.length } : { ок: false, причина: 'Хранилище недоступно' };
}

export default {
  прочитатьЗаключения, сохранитьЗаключение, загрузитьЗаключение, удалитьЗаключение,
  ключЗаключения, подписьЗаключения, собратьФайлЗаключения, разобратьФайлЗаключения, слитьЗаключения,
};
